import type {LengthValue} from '@packages/systems/core/utils/StyleTypes';
import {Qualified} from '@packages/systems/wfdl-Qualified';
import {VariableID} from '.';

import * as StyleVariables from './index';

type BuildParams<T> = {
  name: string;
  value: T;
  id?: string;
  deleted?: boolean;
  origin?: Qualified<VariableID>;
  order?: StyleVariables.AnyVariableType['order'];
  collectionId?: StyleVariables.AnyVariableType['collectionId'];
  modes?: StyleVariables.ColorVariable['modes'];
};

type BuildColorParams = BuildParams<string>;
type BuildLengthParams = BuildParams<LengthValue>;

export function buildColorVariable({
  name,
  value,
  id,
  deleted,
  origin,
  order = null,
  collectionId,
  modes = {},
}: BuildColorParams): StyleVariables.ColorVariable {
  const _id = id
    ? StyleVariables.convertStringToVariableId(id)
    : StyleVariables.generateVariableId();

  return {
    id: _id,
    name,
    type: 'color',
    deleted: Boolean(deleted),
    value: {type: 'color', value},
    createdOn: null,
    updatedOn: null,
    origin,
    order,
    collectionId,
    modes,
    version: 1,
  };
}

export function buildLengthVariable({
  name,
  value,
  id,
  deleted,
  origin,
  collectionId,
}: BuildLengthParams): StyleVariables.LengthVariable {
  const _id = id
    ? StyleVariables.convertStringToVariableId(id)
    : StyleVariables.generateVariableId();

  return {
    id: _id,
    name,
    type: 'length',
    deleted: Boolean(deleted),
    value: {type: 'length', value},
    createdOn: null,
    updatedOn: null,
    origin,
    order: null,
    collectionId,
    modes: {},
    version: 1,
  };
}

const INVALID_CHARS = [
  '\t',
  '\n',
  '\v',
  '\f',
  '\r',
  '!',
  '"',
  '#',
  '$',
  '%',
  '&',
  "'",
  '(',
  ')',
  '*',
  '+',
  ',',
  '-',
  '.',
  '/',
  ':',
  ';',
  '<',
  '=',
  '>',
  '?',
  '@',
  '[',
  '\\',
  ']',
  '^',
  '`',
  '{',
  '|',
  '}',
  '~',
];

export const testCases: {[key: string]: string} = {
  'Pet Attributes/Ear Height': 'pet-attributes--ear-height',
  '🐶 Height': '🐶-height',
  '🐱 Height': '🐱-height',
  '': '',
  ' ': '',
  '  ': '',
  '   ': '',
  a: 'a',
  ' a': 'a',
  'a ': 'a',
  '1': '1',
  ' 1': '1',
  '1 ': '1',
  ' 1 ': '1',
  '01': '01',
  ' 01': '01',
  '01 ': '01',
  ' 01 ': '01',
  _: '_',
  ' _': '_',
  ' _ ': '_',
  あ: 'あ',
  ' あ': 'あ',
  'あ ': 'あ',
  ' あ ': 'あ',
  '💩': '💩',
  ' 💩': '💩',
  '💩 ': '💩',
  ' 💩 ': '💩',
  foo: 'foo',
  Foo: 'foo',
  FOO: 'foo',
  _foo: '_foo',
  foo_: 'foo_',
  _foo_: '_foo_',
  føo: 'føo',
  foöo: 'foöo',
  fübar: 'fübar',
  FÜBÅR: 'fübår',
  '  foo  ': 'foo',
  ' \n\n\t foo \t\n\n ': 'foo',
  ' \n\n\t foo \t\n\n bar': 'foo-bar',
  ' \n\n\t foo \t\n\n bar \t\n\n ': 'foo-bar',
  'foo!': 'foo',
  '!foo': 'foo',
  '@foo': 'foo',
  ' $$ foo $$ ': 'foo',
  'foo-bar': 'foo-bar',
  'foo bar': 'foo-bar',
  'foo  bar': 'foo-bar',
  'Foo Bar': 'foo-bar',
  'foo--bar': 'foo-bar',
  'foo/bar': 'foo--bar',
  'foo /bar': 'foo--bar',
  'foo/ bar': 'foo--bar',
  'foo / bar': 'foo--bar',
  'foo /// bar': 'foo--bar',
  '//foo///bar//': 'foo--bar',
  'foo/bar/baz': 'foo--bar--baz',
  'foo / bar / baz': 'foo--bar--baz',
  'foo /// bar /// baz': 'foo--bar--baz',
  '//foo///bar///baz//': 'foo--bar--baz',
  f$00oo$bar_baz: 'f-00oo-bar_baz',
  '┆┆┆cool┆┆┆beans┆┆┆': '┆┆┆cool┆┆┆beans┆┆┆',

  // We generate a bunch of tests here involving a known list of invalid
  // characters:
  ...INVALID_CHARS.reduce<Record<string, any>>((acc: any, ch: any) => {
    acc[ch] = '';
    acc[ch + 'foo'] = 'foo';
    acc['foo' + ch] = 'foo';
    acc[ch + 'foo' + ch] = 'foo';

    acc[ch + 'foo/bar'] = 'foo--bar';
    acc['foo/bar' + ch] = 'foo--bar';
    acc[ch + 'foo/bar' + ch] = 'foo--bar';

    acc[ch + 'foo' + ch + '/' + ch + 'bar'] = 'foo--bar';
    acc['foo' + ch + '/' + ch + 'bar' + ch] = 'foo--bar';
    acc[ch + 'foo' + ch + '/' + ch + 'bar' + ch] = 'foo--bar';
    return acc;
  }, {}),
};

/**
 * Helper that adds `order` values that work with `fractional-indexing` to test
 * data.
 */
export const addOrderKeys = (
  variables: StyleVariables.AnyVariableType[]
): StyleVariables.VariableMap => {
  const orderKeys = StyleVariables.generateNKeysBetween(
    null,
    null,
    variables.length
  );
  if (orderKeys instanceof Error) throw orderKeys;
  return StyleVariables.VariableMap.from(
    variables.map((variable, idx) => {
      return {...variable, order: orderKeys[idx]!};
    })
  );
};
